/**
 * The guard interstitials (docs/06 §3): what the admin SPA renders when RoleGuard decides
 * the viewer may NOT see the panel.
 *
 * These exist because a null here is a BLANK PAGE. A buyer who follows a link to /admin —
 * cookies are shared across the whole origin, so their session resolves fine — must get an
 * in-app 403 telling them where they belong, not white nothing.
 */
import { useTranslation } from 'react-i18next';
import { Ban, KeyRound, Lock } from 'lucide-react';
import { Button } from '@most/ui';
import { roleHome, useMe } from '@most/auth';
import { CanLoader } from '@/components/bold/Bold';
import { EmptyState } from './States';

/**
 * Rendered while GET /me is in flight. There is no layout to skeleton yet — the shell itself is
 * behind this guard — so it is the one place the admin shows a loader: a can being shaken.
 */
export function GuardLoading() {
  return (
    <div className="flex min-h-screen items-center justify-center" aria-busy="true" aria-live="polite">
      <CanLoader />
    </div>
  );
}

/**
 * Role mismatch. In-app 403 with a way out: a full-page navigation to the viewer's own app
 * root — the SPAs are separate builds, so this is `window.location`, not a router push.
 */
export function Forbidden() {
  const { t } = useTranslation();
  const { data: me } = useMe();

  return (
    <div className="mx-auto max-w-md px-4 py-24">
      <EmptyState
        icon={<Lock className="h-5 w-5" />}
        title={t('errors.forbidden')}
        description={t('errors.forbiddenBody')}
        action={
          me ? (
            <Button variant="outline" onClick={() => window.location.assign(roleHome(me.role))}>
              {t('errors.goToOwnApp')}
            </Button>
          ) : undefined
        }
      />
    </div>
  );
}

/** `status = blocked`. Neutral copy — the WHY is a conversation, not a screen. */
export function AccountUnavailable() {
  const { t } = useTranslation();
  return (
    <div className="mx-auto max-w-md px-4 py-24">
      <EmptyState
        icon={<Ban className="h-5 w-5" />}
        title={t('errors.accountUnavailable')}
        description={t('errors.accountUnavailableBody')}
      />
    </div>
  );
}

/**
 * Admin-created accounts hold a temporary password. In practice this state belongs to buyers
 * and producers (there is no route that creates an admin), but the guard can return it, and
 * "can return it" plus "renders null" is the blank page this file exists to prevent.
 */
export function MustChangePassword() {
  const { t } = useTranslation();
  return (
    <div className="mx-auto max-w-md px-4 py-24">
      <EmptyState
        icon={<KeyRound className="h-5 w-5" />}
        title={t('errors.mustChangePassword')}
        description={t('errors.mustChangePasswordBody')}
      />
    </div>
  );
}
